import { Editor, type AnyExtension, type Extensions } from "@tiptap/core";

import { preprocessContent } from "@/editor/utils/preprocessContent";
import adapters from "@/editor/adapters";
import { Store } from "@/store";

import { doesSupportComposedRangeApi } from "./utils/selection";
import { terminateEditorWorker } from "./worker/workerManager";
import { EditorController } from "./controller";

export function createEditorInstance(
  editorElement: Element,
  extensions: Extensions,
  content: string,
  instanceId: string,
): Editor {
  const editor = new Editor({
    element: editorElement,
    extensions: [...extensions, ...adapters] as AnyExtension[],
    content: preprocessContent(content),
    autofocus: false,
    editable: false,
    injectCSS: true,

    onSelectionUpdate() {
      if (Store.editor.activeInstanceId.value !== instanceId) {
        Store.editor.setActiveInstanceId(instanceId);
      }
    },
    editorProps: {
      attributes: {
        class: "editor-container",
        spellcheck: "false",
        id: instanceId,
      },
    },
  });

  if (doesSupportComposedRangeApi(window)) {
    // OnSelectionUpdate is not reliable on Safari due to shadowRoot triggered events not being propagated to the browser
    editor.view.dom.addEventListener("mousedown", () => {
      if (Store.editor.activeInstanceId.value !== instanceId) {
        Store.editor.setActiveInstanceId(instanceId);
      }
    });
  }

  editor.view.dom.addEventListener("dblclick", event => {
    event.preventDefault();
  });

  Store.editor.registerEditorInstance(instanceId, editor);
  return editor;
}

export function useEditorController(id?: string): EditorController | undefined {
  const editor = Store.editor.getActiveEditor(id);
  return editor ? new EditorController(editor) : undefined;
}

export function removeEditorInstance(instanceId: string): void {
  Store.editor.unregisterEditorInstance(instanceId);

  if (Store.editor.instances.value.size === 0) {
    terminateEditorWorker();
  }
}
